<template>
  <modal
    ref="modalComponent"
    modal-class="choose-parent-goal"
    :show="show"
    size="lg"
    :static="true"
    :lazy="true"
    :submit-button-text="saveOnSubmit ? 'Save changes' : 'Submit'"
    :hide-footer="parentGoalsFetchStatus === LoadingState.ERRORED"
    title="Choose parent goals"
    @submit="onSubmit"
    @hidden="closeModal"
  >
    <div v-if="parentGoalsFetchStatus === LoadingState.ERRORED" class="py-4 pl-3">
      Error while loading parent goals
    </div>
    <div class="text-normal" v-else>
      <div class="goal-box">
        <div class="d-flex" v-for="pGoal in parentGoals" :key="pGoal.id">
          <i class="goal-icon-greyed flex-shrink-0"></i>
          <div>
            <span class="text-medium-1" v-no-capture>{{ pGoal.title }}</span>
            <link-button class="py-0 px-1 text-small-1" @click="onGoalSelection(pGoal, false)"
              >Remove</link-button
            >
          </div>
        </div>
        <i class="arrow-up-icon mt-2" v-if="parentGoals && parentGoals.length > 0"></i>
        <div class="d-flex">
          <i class="goal-icon flex-shrink-0"></i>
          <span class="ml-2 bold text-normal-1" v-if="goal.title" v-no-capture>{{
            goal.title
          }}</span>
          <span class="ml-2 text-normal-1 text-secondary" v-else>[Yet to be filled]</span>
        </div>
      </div>
      <div class="target">
        <label class="d-block">Choose Org, team and timeline</label>
        <div class="d-flex">
          <org-selector-dropdown :selected="subOrg" @change="onSubOrgChange" :org-id="orgId" />
          <org-team-dropdown
            class="ml-3"
            :parent-team-id="subOrg.id"
            :org-item="orgTarget"
            :show-org-group="true"
            @change="target = $event"
            :selected="target"
          />
          <timeline-filter-dropdown class="ml-3" v-model="selectedTimeline" />
        </div>
      </div>
      <div class="goals mt-2">
        <template v-if="goals">
          <div v-if="!isFetching && goals.length === 0" class="text-secondary text-small pl-2 ml-1">
            <span v-if="target.id">No team goals available for the selected filters</span>
            <span v-else>No Org goals available for the selected filters</span>
          </div>
          <template v-for="pGoal in goals">
            <div
              class="goal d-flex justify-content-between align-items-center selectable text-medium-1"
              :class="{ selected: isGoalSelected(pGoal) }"
              :key="pGoal.id"
              v-if="pGoal.id !== goal.id"
            >
              <div class="d-flex text-truncate py-1">
                <i class="goal-icon flex-shrink-0"></i>
                <div class="pl-2 goal-title bold text-truncate" v-no-capture>{{ pGoal.title }}</div>
              </div>
              <div
                class="link-goal-wrapper align-items-center flex-shrink-0 ml-2"
                :class="{ 'd-flex': isGoalSelected(pGoal) }"
              >
                <label
                  class="mb-0 mr-2 text-secondary link-goal-label pointer"
                  :for="'parent-' + pGoal.id"
                  :class="{ 'd-none': isGoalSelected(pGoal) }"
                  >Link as Parent goal</label
                >
                <b-form-checkbox
                  :checked="isGoalSelected(pGoal)"
                  class="pointer"
                  :id="'parent-' + pGoal.id"
                  size="lg"
                  @change="onGoalSelection(pGoal, $event)"
                ></b-form-checkbox>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </modal>
</template>

<script>
import DEFAULT_ERROR from '@/utils/pickError'
import * as R from 'ramda'
import LinkButton from '@/components/buttons/LinkButton'
import OrgSelectorDropdown from '@/components/goal/OrgSelectorDropdown'
import Modal from '@/components/Modal'
import OrgTeamDropdown from '@/components/OrgTeamDropdown'
import TimelineFilterDropdown from '@/components/TimelineFilterDropdown'
import { LoadingState } from '@/constants/common'
import events from '@/constants/events'
import { TOAST_TYPE } from '@/constants/toasts'
import { intervalWithId } from '@/interval'
import EventBusMixin from '@/mixins/EventBusMixin'
import service from '@/service'
import urlManager from '@/urlManager'
import showAPIError from '@/utils/showAPIError'
import showToast from '@/utils/showToast'
import { BFormCheckbox } from 'bootstrap-vue'
import { mapActions } from 'vuex'

/**
 * This component can be used to choose parent goals of a goal
 * Props:
 * - goal
 * @emits
 * - input (v-model)
 */
export default {
  name: 'ParentGoalsAlignment',
  props: ['show', 'goal', 'orgId', 'subOrgId', 'saveOnSubmit', 'selectedParentGoals'],
  mixins: [EventBusMixin],
  model: {
    prop: 'show'
  },
  components: {
    Modal,
    OrgTeamDropdown,
    TimelineFilterDropdown,
    OrgSelectorDropdown,
    BFormCheckbox,
    LinkButton
  },
  data() {
    const subOrg = this.$store.getters.teamById(this.subOrgId)
    return {
      target: {},
      goals: null,
      subOrg,
      orgTarget: { name: 'Org Goals' },
      selectedTimeline: this.getGoalTimeline(),
      isFetching: false,
      isSubmitting: false,
      parentGoals: this.selectedParentGoals,
      parentGoalsFetchStatus: LoadingState.LOADING,
      LoadingState
    }
  },
  methods: {
    ...mapActions(['getGoals', 'patchGoal', 'getGoal']),
    getGoalTimeline() {
      return intervalWithId(this.goal.timeline)
    },
    async refreshGoals() {
      this.isFetching = true
      try {
        const goals = await this.getGoals({
          orgId: this.orgId,
          teamId: this.target?.id || this.subOrg.id,
          options: {
            intervalStart: this.selectedTimeline.start,
            intervalEnd: this.selectedTimeline.end,
            intervalSubset: this.selectedTimeline.subset,
            isArchived: false,
            scopeId: this.subOrg.id
          }
        })
        this.goals = goals.filter(goal => goal.id !== this.goal.id)
      } catch (e) {
        showAPIError(e)
      }
      this.isFetching = false
    },
    async linkParentGoals() {
      try {
        this.isSubmitting = true
        const parentIds = this.parentGoals.map(goal => goal.id)
        await service.client.post(
          urlManager.goalDetail({ orgId: this.orgId, goalId: this.goal.id }),
          { action: 'set_parent_goals', goalIds: parentIds }
        )
        this.closeModal()
        this.getGoal({
          orgId: this.orgId,
          goalId: this.goal.id,
          options: {
            expand: 'parent_goal_ids'
          }
        })
        this.eventBusEmit(events.PARENT_GOAL_ALIGNMENT_CHANGED, {
          childId: this.goal.id,
          parentIds
        })
        showToast('Successfully linked to parent goals')
      } catch (e) {
        showToast(
          R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e),
          TOAST_TYPE.ERROR,
          5000
        )
      }
      this.isSubmitting = false
    },
    onGoalSelection(goal, isSelected) {
      if (isSelected) {
        this.parentGoals = [...this.parentGoals, goal]
      } else {
        this.parentGoals = this.parentGoals.filter(g => g.id !== goal.id)
      }
    },
    isGoalSelected(goal) {
      return this.parentGoals.some(g => g.id === goal.id)
    },
    onSubmit() {
      if (this.saveOnSubmit) {
        this.linkParentGoals()
      } else {
        this.$emit('select', this.parentGoals)
        this.closeModal()
      }
    },
    closeModal() {
      this.$emit('input', false)
    },
    onSubOrgChange(subOrg) {
      this.subOrg = subOrg
      this.target = this.orgTarget
    },
    async refreshParentGoals() {
      if (!this.parentGoals) {
        try {
          this.parentGoalsFetchStatus = LoadingState.LOADING
          this.parentGoals = await this.getGoals({
            orgId: this.orgId,
            options: {
              childId: this.goal.id
            }
          })
          this.parentGoalsFetchStatus = LoadingState.LOADED
        } catch (e) {
          showAPIError(e)
          this.parentGoalsFetchStatus = LoadingState.ERRORED
        }
      }
    }
  },
  async mounted() {
    const goal = this.goal.id ? this.$store.getters.goalById(this.goal.id) : this.goal
    await this.refreshParentGoals()
    this.target = this.$store.getters.teamById(goal.teamId)
  },
  watch: {
    target: 'refreshGoals',
    subOrg: 'refreshGoals',
    selectedTimeline: 'refreshGoals'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.goal-box {
  padding: 16px 18px;
  background: $activeQuaternary;
  box-shadow: 0px 1px 1px #f8f8f8, 0px 2px 3px #c1c1c1;
  border-radius: 10px;

  /deep/ label {
    font-size: 1rem;
    color: $primary !important;
  }
}

.goals {
  min-height: 300px;
  padding-left: 12px;
  padding-top: 8px;
}

.target label {
  padding: 26px 12px 12px 18px;
  margin: 0;
}

.goal-icon {
  margin-right: 4px;
  margin-top: 6px;
}
.goal-icon-greyed {
  margin-right: 12px;
  margin-top: 4px;
}

.goal {
  padding: 10px;
  border-radius: 10px;
  overflow: hidden;
}

.goal:hover {
  background-color: $selectionColor;
  .link-goal-wrapper {
    display: flex;
  }
}

.choose-goal-btn {
  right: 0;
  top: 0;
  bottom: 0;
  background-color: $documentColor;
  font-weight: 600;
  display: none;
  padding: 0 12px 0 4px;

  /deep/ button {
    padding: 1px 12px;
  }
}

.goal:hover .choose-goal-btn {
  background-color: $selectionColor;
  display: flex;
}

.link-goal-wrapper {
  display: none;
}
.link-goal-label {
  transition: color ease-in 100ms;
  &:hover {
    color: $primary !important;
  }
}
</style>
<style lang="scss">
.choose-parent-goal .modal-title {
  padding-left: 12px;
}
</style>
